import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const OnboardingContext = createContext();

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

export const OnboardingProvider = ({ children }) => {
  const [onboardingState, setOnboardingState] = useState({
    isOnboarding: false,
    currentStep: 0,
    totalSteps: 5,
    steps: ['welcome', 'profile', 'preferences', 'featureTour', 'completion'],
    completedSteps: {},
    skippedSteps: {},
    userData: {
      jobTitle: '',
      companyName: '',
      phoneNumber: '',
      useCase: '',
      companySize: '',
      industry: '',
      userPreferences: {
        emailNotifications: true,
        signatureStyle: 'draw',
        documentSharing: 'private',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
      }
    }
  });

  // Get user context to pre-populate data
  const { user } = useAuth();
  
  // Pre-populate onboarding data with existing user data when available
  // Update user data whenever user object changes (not just once)
  useEffect(() => {
    if (user) {
      console.log('🔄 OnboardingContext: Updating with fresh user data:', {
        jobTitle: user.jobTitle,
        companyName: user.companyName,
        phoneNumber: user.phoneNumber,
        useCase: user.useCase,
        isAdminAccount: user.userPreferences?.isAdminAccount
      });

      // Determine if this is an admin account that needs company setup
      const isAdminAccount = user.userPreferences?.isAdminAccount || user.userPreferences?.requiresCompanySetup;
      const steps = isAdminAccount 
        ? ['welcome', 'companySetup', 'profile', 'preferences', 'featureTour', 'completion']
        : ['welcome', 'profile', 'preferences', 'featureTour', 'completion'];

      // Merge optional one-time seed from signup (then remove it)
      let signupSeed = null;
      try {
        const seedRaw = localStorage.getItem('signupProfileSeed');
        if (seedRaw) {
          signupSeed = JSON.parse(seedRaw);
          localStorage.removeItem('signupProfileSeed');
        }
      } catch (e) {
        // ignore parsing errors and continue
      }
      
      setOnboardingState(prev => ({
        ...prev,
        totalSteps: steps.length,
        steps: steps,
        userData: {
          jobTitle: (signupSeed?.jobTitle ?? user.jobTitle) || '',
          companyName: (signupSeed?.companyName ?? user.companyName) || '',
          phoneNumber: user.phoneNumber || '',
          useCase: (signupSeed?.useCase ?? user.useCase) || '',
          companySize: user.companySize || '',
          industry: user.industry || '',
          userPreferences: {
            emailNotifications: user.userPreferences?.emailNotifications ?? true,
            signatureStyle: user.userPreferences?.signatureStyle || 'draw',
            documentSharing: user.userPreferences?.documentSharing || 'private',
            timeZone: user.userPreferences?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
            isAdminAccount: isAdminAccount,
            requiresCompanySetup: user.userPreferences?.requiresCompanySetup || false
          }
        },
        // Also set completed steps based on user's current onboarding status
        completedSteps: user.onboardingSteps || {}
      }));
      
      console.log('✅ OnboardingContext: User data updated successfully', {
        isAdminAccount,
        steps,
        totalSteps: steps.length
      });
    }
  }, [
    user,
    
    user?.id,
    user?.jobTitle,
    user?.companyName, 
    user?.phoneNumber,
    user?.useCase,
    user?.companySize,
    user?.industry,
    user?.userPreferences?.emailNotifications,
    user?.userPreferences?.signatureStyle,
    user?.userPreferences?.documentSharing,
    user?.userPreferences?.timeZone,
    user?.userPreferences?.isAdminAccount,
    user?.userPreferences?.requiresCompanySetup,
    user?.onboardingSteps
  ]); // React to changes in actual user data fields

  // Start onboarding flow
  const startOnboarding = () => {
    setOnboardingState(prev => ({
      ...prev,
      isOnboarding: true,
      currentStep: 0
    }));
  };

  // Stop onboarding flow
  const stopOnboarding = () => {
    setOnboardingState(prev => ({
      ...prev,
      isOnboarding: false,
      currentStep: 0
    }));
  };

  // Go to next step
  const nextStep = () => {
    setOnboardingState(prev => ({
      ...prev,
      currentStep: Math.min(prev.currentStep + 1, prev.totalSteps - 1)
    }));
  };

  // Go to previous step
  const previousStep = () => {
    setOnboardingState(prev => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - 1, 0)
    }));
  };

  // Go to specific step
  const goToStep = (stepIndex) => {
    setOnboardingState(prev => ({
      ...prev,
      currentStep: Math.max(0, Math.min(stepIndex, prev.totalSteps - 1))
    }));
  };

  // Mark step as completed
  const completeStep = (stepName, data = {}) => {
    setOnboardingState(prev => ({
      ...prev,
      completedSteps: {
        ...prev.completedSteps,
        [stepName]: true
      },
      userData: {
        ...prev.userData,
        ...data
      }
    }));
  };

  // Mark step as skipped
  const skipStep = (stepName) => {
    setOnboardingState(prev => ({
      ...prev,
      skippedSteps: {
        ...prev.skippedSteps,
        [stepName]: true
      }
    }));
  };

  // Update user data
  const updateUserData = (data) => {
    setOnboardingState(prev => ({
      ...prev,
      userData: {
        ...prev.userData,
        ...data,
        userPreferences: {
          ...prev.userData.userPreferences,
          ...(data.userPreferences || {})
        }
      }
    }));
  };

  // Reset onboarding state
  const resetOnboarding = () => {
    setOnboardingState(prev => ({
      ...prev,
      isOnboarding: false,
      currentStep: 0,
      completedSteps: {},
      skippedSteps: {},
      userData: {
        jobTitle: '',
        companyName: '',
        phoneNumber: '',
        useCase: '',
        userPreferences: {
          emailNotifications: true,
          signatureStyle: 'draw',
          documentSharing: 'private',
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
        }
      }
    }));
  };

  // Get current step info
  const getCurrentStepInfo = () => {
    const currentStepName = onboardingState.steps[onboardingState.currentStep];
    return {
      name: currentStepName,
      index: onboardingState.currentStep,
      isCompleted: onboardingState.completedSteps[currentStepName],
      isSkipped: onboardingState.skippedSteps[currentStepName],
      isFirst: onboardingState.currentStep === 0,
      isLast: onboardingState.currentStep === onboardingState.totalSteps - 1
    };
  };

  // Check if onboarding is complete
  const isOnboardingComplete = () => {
    const requiredSteps = ['welcome', 'profile', 'preferences', 'featureTour', 'completion'];
    return requiredSteps.every(step => 
      onboardingState.completedSteps[step] || onboardingState.skippedSteps[step]
    );
  };

  // Get progress percentage based on current step
  const getProgress = () => {
    return Math.round(((onboardingState.currentStep + 1) / onboardingState.totalSteps) * 100);
  };

  const value = {
    // State
    onboardingState,
    
    // Actions
    startOnboarding,
    stopOnboarding,
    nextStep,
    previousStep,
    goToStep,
    completeStep,
    skipStep,
    updateUserData,
    resetOnboarding,
    
    // Getters
    getCurrentStepInfo,
    isOnboardingComplete,
    getProgress,
    
    // Computed values
    isOnboarding: onboardingState.isOnboarding,
    currentStep: onboardingState.currentStep,
    totalSteps: onboardingState.totalSteps,
    userData: onboardingState.userData,
    progress: getProgress()
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};